/**
 * Utility functions for tracking events via Google Tag Manager's dataLayer
 * 
 * These functions push events to the dataLayer, which can then be configured in GTM
 * to trigger tags for Google Analytics or any other analytics platform.
 */

/**
 * Pushes an event to Google Tag Manager's dataLayer
 * 
 * This function sends events to GTM, which can then be configured to forward
 * these events to Google Analytics or any other analytics platform.
 * 
 * @param {string} eventName - The name of the event to track
 * @param {Object} eventParams - Additional parameters to send with the event
 * @returns {void}
 * 
 * @example
 * // Track a simple event
 * pushToDataLayer('button_click', { button_name: 'submit' });
 * 
 * // Track a form submission
 * pushToDataLayer('form_submit', { 
 *   form_name: 'contact',
 *   form_location: 'footer'
 * });
 */
export const pushToDataLayer = (eventName, eventParams = {}) => {
  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];
  
  // Push the event to the dataLayer
  window.dataLayer.push({
    event: eventName,
    ...eventParams
  });
  
  // Log in development environment for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('GTM Event:', eventName, eventParams);
  }
};

/**
 * Tracks a page view event via Google Tag Manager
 * 
 * NOTE: Basic page view tracking is already handled by Google Tag Manager by default.
 * This function is only needed for custom page view tracking scenarios.
 * 
 * @param {string} pagePath - The path of the page being viewed
 * @param {string} pageTitle - The title of the page being viewed
 * @returns {void}
 */
export const trackPageView = (pagePath, pageTitle) => {
  pushToDataLayer('page_view', {
    page_path: pagePath,
    page_title: pageTitle
  });
};

/**
 * Tracks a form submission event via Google Tag Manager
 * 
 * @param {string} formName - The name of the form being submitted
 * @param {string} formLocation - The location of the form on the site
 * @param {Object} additionalParams - Any additional parameters to track
 * @returns {void}
 */
export const trackFormSubmit = (formName, formLocation, additionalParams = {}) => {
  pushToDataLayer('form_submit', {
    form_name: formName,
    form_location: formLocation,
    ...additionalParams
  });
};

/**
 * Tracks a button click event via Google Tag Manager
 * 
 * @param {string} buttonName - The name of the button being clicked
 * @param {string} buttonLocation - The location of the button on the site
 * @param {Object} additionalParams - Any additional parameters to track
 * @returns {void}
 */
export const trackButtonClick = (buttonName, buttonLocation, additionalParams = {}) => {
  pushToDataLayer('button_click', {
    button_name: buttonName,
    button_location: buttonLocation,
    ...additionalParams
  });
};

export default {
  pushToDataLayer,
  trackPageView,
  trackFormSubmit,
  trackButtonClick
};
